import React from "react";
import { motion } from "framer-motion";
import { sized } from "../lib/api";
import { useContent } from "../context/ContentContext";

const isYes = (v) => (v || "").trim().toLowerCase() === "yes";

const Attribution = ({ name, location }) => (
  <p className="mt-5 text-sm text-[#4A4947]" data-testid="testimonial-attribution">
    <span className="text-[#1C1B1A]">{name}</span>
    <span className="mx-2 text-[#B35D4B]">—</span>
    <span>{location}</span>
  </p>
);

export function Testimonials() {
  const { p } = useContent();
  const c = p("testimonials").main;

  const all = (c.items || [])
    .filter((t) => !isYes(t.hidden) && (t.quote || "").trim())
    .map((t) => ({
      quotes: [t.quote, t.quote2].filter((q) => (q || "").trim()),
      name: `${t.first_name || ""} ${t.last_initial || ""}`.trim(),
      location: t.location || "",
      image: (t.photo || "").trim() || null,
      image_alt: t.photo_alt || "",
      featured: isYes(t.featured),
    }));

  if (!all.length) return null;

  const featured = all.find((t) => t.featured);
  const rest = all.filter((t) => t !== featured);

  return (
    <section id="testimonials" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-28" data-testid="testimonials-section" aria-label={c.heading}>
      <div className="text-center mb-16">
        <p className="overline mb-3">{c.eyebrow}</p>
        <h2 className="font-display text-4xl sm:text-5xl leading-tight text-[#1C1B1A]">{c.heading}</h2>
        {c.body && <p className="text-[#4A4947] mt-5 max-w-xl mx-auto leading-relaxed">{c.body}</p>}
      </div>

      {featured && (
        <motion.figure
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className={`grid gap-10 items-center mb-16 ${featured.image ? "lg:grid-cols-2" : ""}`}
          data-testid="testimonial-featured"
        >
          {featured.image && (
            <img src={sized(featured.image, 1000)} alt={featured.image_alt} loading="lazy"
              className="w-full h-[420px] object-cover border border-[#E4E1DB]" />
          )}
          <blockquote className={featured.image ? "" : "max-w-3xl mx-auto text-center"}>
            <span aria-hidden="true" className="block font-display text-6xl leading-none text-[#B35D4B]/40 mb-2">&ldquo;</span>
            {featured.quotes.map((q, i) => (
              <p key={i} className="font-display text-2xl sm:text-3xl leading-relaxed text-[#1C1B1A] mb-4">{q}</p>
            ))}
            <figcaption><Attribution name={featured.name} location={featured.location} /></figcaption>
          </blockquote>
        </motion.figure>
      )}

      {rest.length > 0 && (
        <div className={`grid gap-10 md:grid-cols-3 ${featured ? "border-t border-[#E4E1DB] pt-14" : ""}`}>
          {rest.map((t, i) => (
            <motion.figure
              key={`${t.name}-${i}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`testimonial-${i}`}
            >
              {t.image && (
                <img src={sized(t.image, 700)} alt={t.image_alt} loading="lazy"
                  className="w-full h-56 object-cover border border-[#E4E1DB] mb-5" />
              )}
              <blockquote>
                <span aria-hidden="true" className="block font-display text-4xl leading-none text-[#B35D4B]/40 mb-1">&ldquo;</span>
                {t.quotes.map((q, qi) => (
                  <p key={qi} className="text-lg leading-relaxed text-[#1C1B1A] font-light mb-2">{q}</p>
                ))}
                <figcaption><Attribution name={t.name} location={t.location} /></figcaption>
              </blockquote>
            </motion.figure>
          ))}
        </div>
      )}
    </section>
  );
}
